import { useQuery, keepPreviousData } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import type { Venue } from "@features/events/types";

interface UseVenuesParams {
  hcc?: string;
  usedBefore?: boolean;
  query?: string;
  sort?: string;
}

type UseVenuesResult = {
  data: Venue[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  isError: boolean;
  refetch: () => void;
};

/**
 * Hook to fetch venues for an event organizer with filtering and sorting.
 * Returns the full query result for flexibility with pagination and refetching.
 */
export function useVenues({ hcc, usedBefore, query, sort }: UseVenuesParams): UseVenuesResult {
  const { GET } = useApiRequest();
  const queryResult = useQuery<Venue[]>({
    queryKey: ["venues", hcc, usedBefore, query, sort],
    queryFn: async () => {
      if (!hcc) return [];
      const params = new URLSearchParams();
      if (usedBefore) params.append("usedBefore", "true");
      if (query) params.append("query", query);
      if (sort) params.append("sort", sort);
      return GET(`/api/healthcare-companies/${hcc}/e/venues?${params}`);
    },
    enabled: Boolean(hcc),
    placeholderData: keepPreviousData,
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    error: queryResult.error ?? null,
    isError: queryResult.isError,
    refetch: queryResult.refetch,
  } satisfies UseVenuesResult;
}
